import {
  NEEDS_FILING_PERIOD_KEY,
  UNDATED_PERIOD_KEY,
  buildPeriodNodes,
  inPeriod,
  isInstitutionLevel,
  isTemplatePlaceholder,
  periodKeysOf,
  placeholderDisplayName,
  type PeriodNode,
} from './period-tree';
import type { CategoryNode, RoomDocument } from '../api/types';

/**
 * Pure helpers over the room's category tree (GET /rooms/:id/categories) and the
 * document listing (GET /rooms/:id/documents) — the folder view's last levels
 * (Year → Month / Quarter of each document's date tag) and the review panel's
 * "next level".
 *
 * Computed on the client from the listing on purpose: the listing is the room's
 * complete, permission-scoped document set (not paginated), it already carries
 * every document's category ids and its date and period tags, and the page
 * already derives its needs-review count from it. So the period levels need no
 * new endpoint and no change to the categories endpoint, and a node's count is,
 * by construction, exactly what clicking it lists.
 */

/** The synthetic "Uncategorized" bucket is a view artifact, not a real tag. */
export const SYNTHETIC_CATEGORY_IDS: ReadonlySet<string> = new Set([
  'uncategorized',
  'demo-cat-uncat',
]);

/** Category id → parent id (null for a root), over the tree. */
export function categoryParents(nodes: readonly CategoryNode[]): Map<string, string | null> {
  const parentOf = new Map<string, string | null>();
  const walk = (list: readonly CategoryNode[], parent: string | null) => {
    for (const node of list) {
      if (SYNTHETIC_CATEGORY_IDS.has(node.id)) continue;
      parentOf.set(node.id, parent);
      walk(node.children, node.id);
    }
  };
  walk(nodes, null);
  return parentOf;
}

/** Category id → node, over the tree. */
export function categoryNodesById(nodes: readonly CategoryNode[]): Map<string, CategoryNode> {
  const byId = new Map<string, CategoryNode>();
  const walk = (list: readonly CategoryNode[]) => {
    for (const node of list) {
      byId.set(node.id, node);
      walk(node.children);
    }
  };
  walk(nodes);
  return byId;
}

/** The folder names from the top of the tree down to `id` (["Financial",
 * "Bank Statements"]); [] when `id` is not in the tree. */
export function categoryPathNames(nodes: readonly CategoryNode[], id: string): string[] {
  for (const node of nodes) {
    if (node.id === id) return [node.name];
    const below = categoryPathNames(node.children, id);
    if (below.length > 0) return [node.name, ...below];
  }
  return [];
}

function depthOf(id: string, parentOf: ReadonlyMap<string, string | null>): number {
  let depth = 0;
  const seen = new Set<string>();
  let cur: string | null | undefined = id;
  while (cur != null && parentOf.has(cur) && !seen.has(cur)) {
    seen.add(cur);
    depth += 1;
    cur = parentOf.get(cur);
  }
  return depth;
}

/**
 * The document's folder: the deepest of its category edges that is in the tree
 * (a tier-by-tier filing carries every ancestor too). Ties keep input order —
 * the server's own rule (documents/index.ts `mostSpecificCategoryTag`).
 */
export function deepestCategoryId(
  tagIds: readonly string[] | undefined,
  parentOf: ReadonlyMap<string, string | null>,
): string | null {
  let best: string | null = null;
  let bestDepth = 0;
  for (const id of tagIds ?? []) {
    const depth = depthOf(id, parentOf);
    if (depth > bestDepth) {
      best = id;
      bestDepth = depth;
    }
  }
  return best;
}

/** Root-first ids from the top level down to `id` (inclusive). */
export function pathIdsTo(id: string, parentOf: ReadonlyMap<string, string | null>): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  let cur: string | null | undefined = id;
  while (cur != null && !seen.has(cur)) {
    seen.add(cur);
    ids.unshift(cur);
    cur = parentOf.get(cur);
  }
  return ids;
}

/**
 * Whether a document is in a folder, by the one rule the tree's counts use: the
 * document sits at its deepest category edge and rolls up into every ancestor
 * of it. So a folder holds exactly the documents whose deepest edge is the
 * folder or below it. A document also carries the edges of other branches (an
 * account's statement is filed in its account AND in the checklist's shared
 * "[Institution] › Bank Statements" level): those never put it in that other
 * branch's folder, so "Bank not identified › Bank Statements" lists only the
 * statements with no account, as its count says. The synthetic "Uncategorized"
 * node holds the documents with no category in the tree.
 */
export function inFolder(
  doc: Pick<RoomDocument, 'categoryTagIds'>,
  folderId: string,
  parentOf: ReadonlyMap<string, string | null>,
): boolean {
  const deepest = deepestCategoryId(doc.categoryTagIds, parentOf);
  if (SYNTHETIC_CATEGORY_IDS.has(folderId)) return deepest === null;
  return deepest !== null && pathIdsTo(deepest, parentOf).includes(folderId);
}

/**
 * The folder tree's last levels: for each folder, the documents filed directly
 * there (their deepest category) as Year → Month / Quarter nodes
 * (`@dataroom/shared` period-tree), with an "Undated" node for the ones that
 * carry no date tag. A folder gets period nodes when any of its own documents
 * is dated, or when it also has subfolders, so the counts always add up: a
 * folder's count is its subfolders' counts plus its period nodes' counts. A
 * leaf folder whose documents are all undated gets none (a lone "Undated" row
 * would only repeat the folder).
 *
 * `branchIds`: the folders that show subfolders in the tree.
 */
export function periodNodesByCategory(
  docs: readonly Pick<
    RoomDocument,
    'categoryTagIds' | 'dataDate' | 'period' | 'classificationStatus'
  >[],
  parentOf: ReadonlyMap<string, string | null>,
  branchIds: ReadonlySet<string> = new Set(),
): Map<string, PeriodNode[]> {
  const byFolder = new Map<
    string,
    Pick<RoomDocument, 'dataDate' | 'period' | 'classificationStatus'>[]
  >();
  for (const doc of docs) {
    const folder = deepestCategoryId(doc.categoryTagIds, parentOf);
    if (!folder) continue;
    const list = byFolder.get(folder) ?? [];
    list.push(doc);
    byFolder.set(folder, list);
  }
  const nodes = new Map<string, PeriodNode[]>();
  for (const [folder, list] of byFolder) {
    // Documents resting here only until they are filed further down get ONE
    // "Needs filing" row, never year rows (which read as "filed here").
    const unfiled = list.filter(needsFilingHere);
    const filed = list.filter((d) => !needsFilingHere(d));
    const out: PeriodNode[] = [];
    if (unfiled.length > 0) {
      out.push({
        key: NEEDS_FILING_PERIOD_KEY,
        kind: 'needs_filing',
        label: 'Needs filing',
        fullLabel: 'Needs filing',
        count: unfiled.length,
        children: [],
      });
    }
    const dated = filed.some((d) => periodKeysOf(d)[0] !== UNDATED_PERIOD_KEY);
    if (dated || ((branchIds.has(folder) || unfiled.length > 0) && filed.length > 0)) {
      out.push(...buildPeriodNodes(filed));
    }
    if (out.length > 0) nodes.set(folder, out);
  }
  return nodes;
}

/** A document that rests at its folder only until it is filed (the listing's "Needs filing"). */
function needsFilingHere(doc: Pick<RoomDocument, 'classificationStatus'>): boolean {
  return doc.classificationStatus === 'low_confidence' || doc.classificationStatus === 'failed';
}

/** Whether a document is in a period node: filed directly at `folderId`, in `periodKey`. */
export function inFolderPeriod(
  doc: Pick<RoomDocument, 'categoryTagIds' | 'dataDate' | 'period' | 'classificationStatus'>,
  folderId: string,
  periodKey: string,
  parentOf: ReadonlyMap<string, string | null>,
): boolean {
  if (deepestCategoryId(doc.categoryTagIds, parentOf) !== folderId) return false;
  if (periodKey === NEEDS_FILING_PERIOD_KEY) return needsFilingHere(doc);
  return !needsFilingHere(doc) && inPeriod(doc, periodKey);
}

/**
 * The tree as the folder view shows it: a template placeholder category
 * ("[Account Name]", "[Bank Account]" …) holding no document is dropped (the AI
 * and the questionnaire fill it; it is not a folder), and one that does hold
 * documents reads "Unassigned account". In the ACCOUNT TREE — below the category
 * holding "[Institution]" (Bank Docs, ADR-0037 update 2026-10-15) — a folder
 * holding no document is not shown at all: a bank shows only the statement types
 * and accounts that hold documents (an empty "Bank Contact Information › ••4321"
 * is clutter), and "[Institution]" reads "Bank not identified". DISPLAY ONLY:
 * the counts are the server's (what the viewer may see); nothing is filed or
 * granted here. Every other node is unchanged.
 */
export function folderViewTree(
  nodes: readonly CategoryNode[],
  inAccountTree = false,
): CategoryNode[] {
  const out: CategoryNode[] = [];
  for (const node of nodes) {
    const placeholder = isTemplatePlaceholder(node.name);
    if ((placeholder || inAccountTree) && node.documentCount === 0) continue;
    const holdsBanks = node.children.some((k) => isInstitutionLevel(k.name));
    out.push({
      ...node,
      name: placeholder ? placeholderDisplayName(node.name) : node.name,
      children: folderViewTree(node.children, inAccountTree || holdsBanks),
    });
  }
  return out;
}

/** The ids of the folders that have subfolders. */
export function branchCategoryIds(nodes: readonly CategoryNode[]): Set<string> {
  const ids = new Set<string>();
  const walk = (list: readonly CategoryNode[]) => {
    for (const node of list) {
      if (node.children.length > 0) ids.add(node.id);
      walk(node.children);
    }
  };
  walk(nodes);
  return ids;
}

/** Flatten the category tree into an id→"A / B / C" path map so a proposal's
 * `tagId` (or a filed `categoryTagIds` entry) resolves to where it would file. */
export function categoryPathsById(
  nodes: readonly CategoryNode[],
  parent: string[] = [],
  into: Map<string, string> = new Map(),
): Map<string, string> {
  for (const node of nodes) {
    const path = [...parent, node.name];
    into.set(node.id, path.join(' / '));
    categoryPathsById(node.children, path, into);
  }
  return into;
}
